import {Injectable} from '@angular/core';
import {Category, RoutingService, TranslationService} from '@spartacus/core';
import {NavModel} from 'ng-design-system';
import {JsonService} from '@dcp/configurator';

@Injectable({
  providedIn: 'root'
})

export class CategoryService extends JsonService {
  erpAndFinanceErpSub: Array<Category> = [
    {translationKey: 'all-in-erp-and-finance', code: 'erp-and-finance'},
    {translationKey: 'sap-s-4hana'},
    {translationKey: 'erp-for-small-and-midsize-enterprises'},
    {translationKey: 'financial-planning-and-analysis'},
    {translationKey: 'accounting-and-financial-close'},
    {translationKey: 'treasury-management'},
    {translationKey: 'accounts-receivable-billing-and-revenue-management'},
    {translationKey: 'governance-risk-compliance-and-cybersecurity'},
  ];

  crmAndCustomerExperienceErpSub: Array<Category> = [
    {translationKey: 'all-in-crm-and-customer-experience', code: 'crm-and-customer-experience'},
    {translationKey: 'customer-data'},
    {translationKey: 'marketing'},
    {translationKey: 'commerce'},
    {translationKey: 'sales'},
    {translationKey: 'service'},
  ];

  spendManagementErpSub: Array<Category> = [
    {translationKey: 'all-in-spend-management', code: 'spend-management'},
    {translationKey: 'supplier-management'},
    {translationKey: 'sourcing-and-contracts'},
    {translationKey: 'procurement'},
    {translationKey: 'external-workforce-and-services'},
    {translationKey: 'invoices-and-payments'},
    {translationKey: 'travel-and-expense'},
  ];

  supplyChainManagementErpSub: Array<Category> = [
    {translationKey: 'all-in-supply-chain-management', code: 'supply-chain-management'},
    {translationKey: 'supply-chain-planning'},
    {translationKey: 'supply-chain-logistics'},
    {translationKey: 'manufacturing'},
    {translationKey: 'product-lifecycle-management'},
    {translationKey: 'enterprise-asset-management'},
  ];

  humanCapitalManagementErpSub: Array<Category> = [
    {translationKey: 'all-in-human-capital-management', code: 'human-capital-management'},
    {translationKey: 'employee-experience-management'},
    {translationKey: 'core-hr-and-payroll'},
    {translationKey: 'talent-management'},
    {translationKey: 'hr-analytics-and-workforce-planning'},
  ];

  businessTechnologyPlatformErpSub: Array<Category> = [
    {translationKey: 'all-in-business-technology-platform', code: 'business-technology-platform'},
    {translationKey: 'database-and-data-management'},
    {translationKey: 'application-development-and-automation'},
    {translationKey: 'analytics'},
    {translationKey: 'intelligent-technologies'},
  ];

  categories: Array<Category> = [
    {translationKey: 'erp-and-finance', subCategories: this.erpAndFinanceErpSub, icon: true},
    {translationKey: 'crm-and-customer-experience', subCategories: this.crmAndCustomerExperienceErpSub, icon: true},
    {translationKey: 'spend-management', subCategories: this.spendManagementErpSub, icon: true},
    {translationKey: 'supply-chain-management', subCategories: this.supplyChainManagementErpSub, icon: true},
    {translationKey: 'human-capital-management', subCategories: this.humanCapitalManagementErpSub, icon: true},
    {translationKey: 'business-technology-platform', subCategories: this.businessTechnologyPlatformErpSub, icon: true},

    // L1 categories with URL
    {translationKey: 'services-and-support', icon: true},
    {translationKey: 'training-and-adoption', icon: true},
  ];

  constructor(
    private routingService: RoutingService,
    private translationService: TranslationService
  ) {
    super();
  }

  getCategories(): Array<Category> {
    // Returning a deep copy so the changes on the categories don't influence other components
    return this.parseSafely(JSON.stringify(this.categories));
  }

  displayProductsOfCategory(categoryId: string): void {
    this.routingService.go({
        cxRoute: 'search',
        params: {
          query: '',
        },
      },
      {
        queryParams: {
          query: ':relevance:category:' + categoryId,
          sortCode: 'relevance'
        }
      },
    );
  }

  /**
   * Places the data of Category objects into NavModels
   *
   * @param categories array of categories to convert
   */
  categoriesToNavModels(categories: Category[]): NavModel[] {
    const navModels: NavModel[] = [];
    for (const category of categories) {
      navModels.push(
        this.categoryToNavModel(category)
      );
    }
    return navModels;
  }

  /**
   * Places the data of a Category object into a NavModel
   *
   * @param category to convert
   */
  categoryToNavModel(category: Category): NavModel {
    return new NavModel(
      category.translationKey,
      this.translationService.translate(`categories.${category.translationKey}`)
    );
  }

  /**
   * Checks if a category should be visible to the customer according to the json configuration
   *
   * @param category category to check
   */
  isDisplayed(category: Category): boolean {
    const configuration = this.getJsonDataValue(category?.data, 'configuration')?.isDisplayed;
    const isDisplayed = configuration === undefined ? true : !!configuration;
    /**
     * We have a gap here as categories are automatically created during product creation from SLM / SCPI but json is not yet created
     * Therefore categories starting with audience_ are exempt by hardcoding the code for now
     */
    const audience = !category?.code?.startsWith('audience_') && !category?.id?.startsWith('audience_');

    return isDisplayed && audience;
  }
}
